import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { COMPANY } from "@/constants/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("et_admin_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-6 py-24" data-testid="admin-login-page">
      <img src={COMPANY.logo} alt={`${COMPANY.name} logo`} className="h-14 w-14 rounded-full object-cover ring-1 ring-navy-600" />
      <h1 className="mt-6 flex items-center gap-2 text-2xl font-bold text-white">
        <LockKeyhole className="h-5 w-5 text-brand" /> Enquiry Inbox
      </h1>
      <p className="mt-2 text-sm text-slate-400">Authorised staff only.</p>

      <form onSubmit={onSubmit} className="mt-8 w-full rounded-lg border border-navy-600 bg-navy-800 p-8" data-testid="admin-login-form">
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Email</label>
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@excellentraders.com"
          className="border-navy-600 bg-navy text-white placeholder:text-slate-500 focus-visible:ring-brand"
          data-testid="admin-email-input"
        />
        <label className="mb-1.5 mt-5 block text-xs font-medium uppercase tracking-wider text-slate-400">Password</label>
        <Input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="border-navy-600 bg-navy text-white placeholder:text-slate-500 focus-visible:ring-brand"
          data-testid="admin-password-input"
        />
        {error && (
          <p className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={loading}
          className="mt-6 w-full bg-brand font-semibold text-navy hover:bg-brand hover:shadow-[0_0_24px_rgba(0,208,132,0.35)]"
          data-testid="admin-login-submit"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {loading ? "Signing in..." : "Sign in"}
        </Button>
      </form>
    </div>
  );
}
